<template>
  <div class="space-y-6">
    <!-- Header -->
    <div>
      <h3 class="text-xl font-bold text-hui-foreground">Настройки Яндекс</h3>
      <p class="text-sm text-hui-foreground/60 mt-1">Настройте параметры OAuth Яндекс</p>
    </div>

    <div v-if="loading" class="text-sm text-hui-foreground/60">Загрузка настроек…</div>

    <template v-else>
      <form class="space-y-4" @submit.prevent="saveSettings">
        <div class="flex items-center justify-between rounded-xl border border-hui-divider p-3">
          <div>
            <p class="font-medium text-hui-foreground">Авторизация через Яндекс</p>
            <p class="text-xs text-hui-foreground/60">Когда выключено — кнопка входа через Яндекс скрыта у клиента</p>
          </div>
          <UiKitSwitch v-model="enabled" color="success" />
        </div>

        <UiKitInput
          v-model="form.client_id"
          label="Client ID"
          placeholder="Введите Client ID"
        />

        <UiKitInput
          v-model="form.redirect_url"
          label="Redirect URL"
          placeholder="https://yourdomain.com/auth/yandex/callback"
        />

        <div class="flex justify-end gap-3 pt-4 border-t border-hui-divider">
          <UiKitButton type="button" color="default" variant="flat" @click="closeModal">
            Отмена
          </UiKitButton>
          <UiKitButton type="submit" color="primary" :loading="loading">
            Сохранить настройки
          </UiKitButton>
        </div>
      </form>
    </template>
  </div>
</template>

<script setup lang="ts">
import consola from 'consola';
import { ref, onMounted } from 'vue';
import { useKitToast } from '@shared/ui/kit';
import { APIAdminInstitution } from '~/fsd-pages/admin/api';
import { useModalStore } from '@shared/model';
import type { YandexSettings } from '@entities/institution';

const emit = defineEmits<{
  'update:size': [size: string]
}>();

const { toast: kitToast } = useKitToast();
const toast = {
  add: (o: { title?: string; description?: string; color?: string; timeout?: number }) =>
    kitToast({ color: o.color === 'green' ? 'success' : o.color === 'red' ? 'danger' : o.color === 'orange' ? 'warning' : 'default', title: o.title, description: o.description, timeout: o.timeout }),
};

const modalStore = useModalStore();
const loading = ref(false);

const form = ref<YandexSettings>({
  client_id: '',
  redirect_url: '',
});
// Мастер-флаг включения авторизации (institution_yandex.is_active)
const enabled = ref(false);

const loadSettings = async () => {
  try {
    loading.value = true;
    const response = await APIAdminInstitution.getYandexSettings();

    // Данные приходят в объекте yandex
    const settings = (response.yandex || response) as YandexSettings & { is_active?: boolean };

    form.value = { ...settings };
    enabled.value = settings.is_active !== false;
  } catch (error) {
    consola.error('Ошибка загрузки настроек Яндекс:', error);
    toast.add({ title: 'Ошибка', description: 'Ошибка загрузки настроек', color: 'red' });
  } finally {
    loading.value = false;
  }
};

const saveSettings = async () => {
  try {
    loading.value = true;

    await APIAdminInstitution.updateYandexSettings(form.value);
    await APIAdminInstitution.updateYandexAuthEnabled(enabled.value);
    toast.add({ title: 'Сохранено', description: 'Настройки Яндекс успешно сохранены', color: 'green' });

    setTimeout(() => {
      closeModal();
    }, 1500);
  } catch (error) {
    consola.error('Ошибка сохранения настроек Яндекс:', error);
    toast.add({ title: 'Ошибка', description: 'Ошибка сохранения настроек', color: 'red' });
  } finally {
    loading.value = false;
  }
};

const closeModal = () => {
  modalStore.close();
};

onMounted(() => {
  emit('update:size', 'l');
  loadSettings();
});
</script>
